import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  BookOpen, 
  Users, 
  TrendingUp, 
  GraduationCap,
  BarChart3,
  Clock,
  CheckCircle,
  Loader2
} from 'lucide-react';

const CourseAnalyticsSummary = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.orgId) {
      fetchAnalytics();
    }
  }, [user]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/organizations/${user.orgId}/analytics/overview`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Use fallback data if API fails
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const overview = analytics?.overview || {
    totalCourses: 0,
    publishedCourses: 0,
    totalUsers: 0,
    activeLearners: 0,
    avgCompletion: 0,
    totalEnrollments: 0
  };

  const topCourses = analytics?.topCourses?.slice(0, 3) || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-orange-500" />
        <h3 className="text-lg font-semibold text-gray-900">Course Analytics</h3>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-orange-600" />
              <span className="text-xs text-gray-600">Total Courses</span>
            </div>
            <p className="text-2xl font-bold text-orange-600 mt-1">{overview.totalCourses}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xs text-gray-600">Published</span>
            </div>
            <p className="text-2xl font-bold text-green-600 mt-1">{overview.publishedCourses}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-gray-600">Enrollments</span>
            </div>
            <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="sidebar-enrollments-count">{overview.totalEnrollments}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <span className="text-xs text-gray-600">Avg Completion</span>
            </div>
            <p className="text-2xl font-bold text-purple-600 mt-1">{overview.avgCompletion}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Courses */}
      {topCourses.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-orange-500" />
            Top Courses by Enrollment
          </h4>
          <div className="space-y-2">
            {topCourses.map((course, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex items-center justify-center w-5 h-5 bg-orange-100 text-orange-600 rounded-full text-xs font-bold">
                    {index + 1}
                  </span>
                  <span className="text-sm text-gray-700 truncate">{course.name}</span>
                </div>
                <Badge variant="secondary" className="text-xs shrink-0">
                  {course.enrollments} enrolled
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Quick Stats</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Draft Courses</span>
            <span className="font-medium">{overview.totalCourses - overview.publishedCourses}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Active Learners (30d)</span>
            <span className="font-medium" data-testid="sidebar-active-learners">{overview.activeLearners}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Total Users</span>
            <span className="font-medium">{overview.totalUsers}</span>
          </div>
        </div>
      </div>

      {/* Tip */}
      <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
        <p className="text-xs text-orange-700">
          <strong>Tip:</strong> Select a course from the list to view detailed information and actions.
        </p>
      </div>
    </div>
  );
};

export default CourseAnalyticsSummary;
